// Utilities for validating the post-login redirect target passed as
// `?returnUrl=` or `?afterLoginPage=` on the /login route.
//
// Two failure modes we're defending against:
//   1. Open redirect abuse — an attacker crafts a login URL whose
//      `returnUrl` points to a phishing site on another domain.
//   2. Post-auth XSS — `returnUrl` carrying a `javascript:` URL that
//      would execute in the authenticated user's context if we blindly
//      set `window.location.href = returnUrl`.
//
// Rules:
//   - Relative paths starting with "/" (but NOT "//") are same-SPA and
//     fine to hand to react-router's navigate().
//   - Absolute URLs are allowed ONLY when the host matches the current
//     page's registrable root domain (eTLD+1, approximated as the last
//     two dotted labels). This lets a user at dncscrub.com bounce to
//     core.dncscrub.com / classic.dncscrub.com but NOT to evil.com.
//     Deriving the root from the current location means whitelabel
//     deployments (e.g. nxxg.dncscrub.com) work without code changes.
//   - Protocol must be https. http is rejected to prevent downgrade,
//     and javascript:/data:/file: etc. are rejected to block XSS.
//   - The redirect target must not itself be /login — that would loop.

// Extract the registrable root of a hostname by taking the last two
// labels. Good enough for our domains (dncscrub.com, dnc.com). Does
// NOT handle public suffixes like .co.uk — we don't host on those.
export const getRegistrableRoot = (hostname: string): string => {
  const parts = hostname.toLowerCase().split(".");
  if (parts.length < 2) return hostname.toLowerCase();
  return parts.slice(-2).join(".");
};

export type ReturnUrlKind = "relative" | "absolute" | "invalid";

// Classify a decoded returnUrl/afterLoginPage value into how we should
// navigate. Pass the current hostname so the allowlist stays in sync
// with where the login page is actually being served from.
export const classifyReturnUrl = (
  returnUrl: string | null,
  currentHostname: string,
): ReturnUrlKind => {
  if (!returnUrl) return "invalid";
  // Block /login loops regardless of whether it's a relative path or
  // an absolute URL whose pathname starts with /login — checked again
  // below for the absolute case.
  if (returnUrl.toLowerCase().startsWith("/login")) return "invalid";

  // No scheme separator => treat as a relative path. Reject "//host"
  // protocol-relative URLs, which would escape to another host.
  if (!returnUrl.includes("://")) {
    return returnUrl.startsWith("/") && !returnUrl.startsWith("//")
      ? "relative"
      : "invalid";
  }

  let parsed: URL;
  try {
    parsed = new URL(returnUrl);
  } catch {
    return "invalid";
  }

  // https only. http is a downgrade; javascript:/data:/file:/etc. are
  // the XSS vectors we're defending against.
  if (parsed.protocol !== "https:") return "invalid";

  // Host must share the registrable root with the current page. Using
  // a strict equality OR a ".{root}" suffix check prevents the classic
  // trick of registering `dncscrub.com.evil.com` to pass a naive
  // endsWith("dncscrub.com") test.
  const allowedRoot = getRegistrableRoot(currentHostname);
  const host = parsed.hostname.toLowerCase();
  const onAllowedDomain =
    host === allowedRoot || host.endsWith(`.${allowedRoot}`);
  if (!onAllowedDomain) return "invalid";

  // Same /login-loop guard for absolute URLs.
  if (parsed.pathname.toLowerCase().startsWith("/login")) return "invalid";

  return "absolute";
};
